/**
 * SpecLens projects kept in this browser, and nowhere else.
 *
 * Per browser and per machine, like PlanForge's saved projects (project-store.ts): no server
 * copy of a bid is made, so the owner is the only person who can open it, and the Export button
 * is how a project is backed up or moved. Its own database, so PlanForge's schema version is
 * untouched. Every call degrades to a no-op - a private window or blocked site data makes
 * IndexedDB throw, and losing the ability to save is a worse experience, not a broken one.
 */

import { restoreProject, summaryOf, type ProjectSummary, type SpecLensProject } from "./speclens-project.ts";

const DATABASE = "plan-forge-speclens";
const PROJECTS = "projects";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(PROJECTS)) request.result.createObjectStore(PROJECTS, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function transact<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(PROJECTS, mode);
    const request = work(transaction.objectStore(PROJECTS));
    transaction.oncomplete = () => {
      db.close();
      resolve(request.result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

export async function saveSpeclensProject(project: SpecLensProject): Promise<boolean> {
  try {
    await transact("readwrite", (store) => store.put({ ...project, updatedAt: new Date().toISOString() }));
    return true;
  } catch {
    return false;
  }
}

export async function listSpeclensProjects(): Promise<readonly ProjectSummary[]> {
  try {
    const all = await transact("readonly", (store) => store.getAll());
    return all
      .map((value) => restoreProject(value))
      .filter((project): project is SpecLensProject => project !== null)
      .map(summaryOf)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch {
    return [];
  }
}

export async function loadSpeclensProject(id: string): Promise<SpecLensProject | null> {
  try {
    return restoreProject(await transact("readonly", (store) => store.get(id)));
  } catch {
    return null;
  }
}

export async function deleteSpeclensProject(id: string): Promise<void> {
  try {
    await transact("readwrite", (store) => store.delete(id));
  } catch {
    // Nothing to do: the project simply stays in the list.
  }
}
